import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import OtpInput from "react-otp-input";
import { AuthButton } from "./AuthLayout";
import { verifyEmail, resendEmailVerification } from "../api/auth";

export type ERROR_TYPE = "VALIDATION_OTP_INVALID";

export function VerifyEmailForm({ email, onSuccess }: { email: string, onSuccess: () => void }) {
    const { t } = useTranslation();
    const [otp, setOtp] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [resendCooldown, setResendCooldown] = useState(120); // 120 seconds cooldown
    const [resendLoading, setResendLoading] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);

    // Countdown timer effect
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setInterval(() => {
                setResendCooldown((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [resendCooldown]);

    const handleVerify = async () => {
        if (!otp || otp.length !== 6) {
            setError(t('auth.verifyEmail.errors.invalidCode'));
            return;
        }
        setLoading(true);
        setError("");
        try {
            await verifyEmail({ email, code: otp });
            onSuccess();
        } catch (e: any) {
            const errorCode = e.response?.data?.error?.code as ERROR_TYPE;
            const errorMessage = e.response?.data?.error?.message;
            if (errorCode === "VALIDATION_OTP_INVALID" && errorMessage === "Invalid code") {
                setError(t('auth.verifyEmail.errors.codeInvalid'));
            } else if (errorCode === "VALIDATION_OTP_INVALID" && errorMessage === "Code expired") {
                setError(t('auth.verifyEmail.errors.codeExpired'));
            }
            else {
                setError(t('auth.verifyEmail.errors.verificationFailed'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResendLoading(true);
        setResendSuccess(false);
        setError("");
        try {
            await resendEmailVerification(email);
            setResendCooldown(120); // Reset to 120 seconds
            setResendSuccess(true);
            // Hide success message after 3 seconds
            setTimeout(() => setResendSuccess(false), 3000);
        } catch (e: any) {
            setError(e.response?.data?.message || t('auth.verifyEmail.errors.resendFailed'));
        } finally {
            setResendLoading(false);
        }
    };

    // Format time as MM:SS
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="w-full flex flex-col items-center">
            <h1 className="font-script text-6xl text-[#338585] mb-4">{t('auth.verifyEmail.title')}</h1>
            <p className="text-gray-500 text-center mb-8 text-sm">
                {t('auth.verifyEmail.subtitle')} <br />
                <span className="text-[#D85C57]">{email}</span> {t('auth.verifyEmail.verification')}
            </p>

            {/* Error Alert - Prominently displayed at the top */}
            {error && (
                <div className="w-full mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-medium text-red-800">
                                {error}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Alert for Resend */}
            {resendSuccess && (
                <div className="w-full mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg shadow-sm">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-medium text-green-800">
                                {t('auth.verifyEmail.success')}
                            </p>
                        </div>
                    </div>
                </div>
            )}


            <OtpInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                renderSeparator={<span className="w-4"></span>}
                renderInput={(props) => <input {...props} className="!w-12 !h-12 text-2xl text-center rounded-lg border-none shadow-sm focus:ring-1 focus:ring-[#E86C31] outline-none text-gray-700 bg-white" />}
                containerStyle={{ justifyContent: 'center', marginBottom: '2rem' }}
                inputType="tel"
            />


            <div className="w-full max-w-[300px]">
                <AuthButton disabled={loading || otp.length !== 6} onClick={handleVerify}>
                    {loading ? t('auth.verifyEmail.verifying') : t('auth.verifyEmail.verifyButton')}
                </AuthButton>
            </div>

            <div className="mt-8 text-sm text-gray-600">
                {t('auth.verifyEmail.didntReceiveCode')} {" "}
                <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendCooldown > 0 || resendLoading}
                    className={`${resendCooldown > 0 || resendLoading ? 'text-gray-400 cursor-not-allowed' : 'text-[#D85C57] hover:underline'}`}
                >
                    {resendLoading ? t('auth.verifyEmail.sending') : t('auth.verifyEmail.resendAgain')}
                </button>
            </div>
            {resendCooldown > 0 && (
                <div className="mt-2 text-xs text-gray-400">
                    {t('auth.verifyEmail.requestNewCode')} {formatTime(resendCooldown)}
                </div>
            )}
        </div>
    );
}
